import React, { useEffect, useRef, useState } from "react";
import { resolveAudioUrl } from "../lib/api";

const formatTime = (s) => {
  if (!isFinite(s) || s < 0) return "0:00";
  const mm = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${mm}:${ss.toString().padStart(2, "0")}`;
};

export default function AudioPlayer({ demo, index, isActive, onActivate }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!isActive && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setPlaying(false);
    }
  }, [isActive]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      onActivate?.();
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const onSeek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    a.currentTime = ratio * duration;
  };

  const pct = duration ? (current / duration) * 100 : 0;

  return (
    <div
      className={`group border-t border-white/10 last:border-b py-6 md:py-8 transition-colors ${
        playing ? "bg-white/[0.02]" : "hover:bg-white/[0.015]"
      }`}
      data-testid={`demo-row-${index}`}
    >
      <div className="grid grid-cols-12 items-center gap-4 md:gap-6 px-2">
        {/* number */}
        <div className="col-span-2 md:col-span-1 text-[#9CA3AF] text-sm tracking-widest">
          {String(index + 1).padStart(2, "0")}
        </div>

        {/* play button */}
        <div className="col-span-2 md:col-span-1">
          <button
            onClick={toggle}
            className={`w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center transition-all ${
              playing
                ? "bg-[#F5F5F0] text-[#0a1024]"
                : "bg-[#F25C32] text-white hover:scale-105"
            }`}
            aria-label={playing ? "Pausar" : "Reproducir"}
            data-testid={`demo-play-button-${index}`}
          >
            {playing ? (
              <i className="fa-solid fa-pause text-base" />
            ) : (
              <i className="fa-solid fa-play text-base ml-0.5" />
            )}
          </button>
        </div>

        {/* title + category */}
        <div className="col-span-8 md:col-span-4">
          <div className="heading-display text-2xl md:text-3xl text-[#F5F5F0]" data-testid={`demo-title-${index}`}>
            {demo.title}
          </div>
          <div className="text-xs tracking-[0.25em] uppercase text-[#F25C32] mt-1">
            {demo.category}
          </div>
        </div>

        {/* waveform/progress */}
        <div className="col-span-12 md:col-span-5 mt-3 md:mt-0">
          <div
            className="relative h-2 bg-white/10 cursor-pointer rounded-full overflow-hidden"
            onClick={onSeek}
            data-testid={`demo-progress-${index}`}
          >
            <div
              className="absolute inset-y-0 left-0 bg-[#F25C32] rounded-full transition-[width] duration-100"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-2 flex justify-between text-xs text-[#9CA3AF] tracking-wide">
            <span>{formatTime(current)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* duration on desktop */}
        <div className="hidden md:block md:col-span-1 text-right text-sm text-[#9CA3AF] tracking-widest">
          {formatTime(duration)}
        </div>
      </div>

      <audio
        ref={audioRef}
        src={resolveAudioUrl(demo.url)}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime || 0)}
        onEnded={() => { setPlaying(false); setCurrent(0); }}
      />
    </div>
  );
}
